import { Checkbox, Input, Select, Switch, Text, Textarea } from "@ttc3k/trekker";
import { EnumSurveyQuestionType, Maybe, SurveyQuestion } from "gql/generated";
import { i18nNamespace, loadLanguagePacks } from "i18n";
import _ from "lodash";
import { lazy, memo, ReactNode, Suspense, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { DateInput } from "shared/components/atoms/DateInput/DateInput";
import { getMultilingualString } from "shared/utils";
import { css } from "styled-system/css";
import { Box, Flex } from "styled-system/jsx";
import { AddressSearchInput } from "./AddressSearchInput";
import {
  getActiveSurveyQuestionOptions,
  isMultiOptionBooleanQuestion,
} from "./surveyQuestionCheckbox";
import { isSurveyDesignFieldType } from "./surveyQuestionDesign";
import { SurveyDesignField } from "./SurveyDesignField";
import { WhereaboutsSearchInput } from "./WhereaboutsSearchInput";

await loadLanguagePacks([i18nNamespace.Booker]);

const TextEditor = lazy(() =>
  import("shared/components/molecules/TextEditor").then(module => ({
    default: module.TextEditor,
  })),
);

type SurveyQuestionInputProps = {
  question: SurveyQuestion;
  response: string[] | Maybe<string>[];
  onResponseChange: (questionId: string, response: string[]) => void;
  organizationId?: string;
  /** Validation message shown in red beneath the field. */
  error?: string;
  /** Helper text shown between the question name and the input. */
  description?: string;
  /** Prefix for input ids when the same question is shown more than once (e.g. per business). */
  idScope?: string;
  /** Checkbox groups: the most choices that can be ticked. */
  maxChoices?: number | null;
};

export const SurveyQuestionInput = memo(({
  question,
  response,
  onResponseChange,
  organizationId,
  error,
  description,
  idScope,
  maxChoices,
}: SurveyQuestionInputProps) => {
  const inputId = idScope ?? question._id;
  const questionName =
    getMultilingualString(question.name) + (question.required ? " *" : "");
  const { t } = useTranslation("booker", { keyPrefix: "QUESTIONNAIRE" });

  const descriptionText = description ? (
    <Text visual="smallRegular" color="text.mid" whiteSpace="pre-wrap">
      {description}
    </Text>
  ) : null;
  // Trekker inputs render their own label with no slot beneath it, so with a
  // description the name is drawn here and the input gets no label.
  const nameHeader = (
    <Flex flexDir="column" gap="25">
      <Text visual="bodyMedium" color="text.dark" whiteSpace="pre-wrap">
        {questionName}
      </Text>
      {descriptionText}
    </Flex>
  );
  const inputLabel = description ? undefined : questionName;
  const withNameHeader = (input: ReactNode) =>
    description ? (
      <Flex flexDir="column" gap="100">
        {nameHeader}
        {input}
      </Flex>
    ) : (
      input
    );

  const getContent = useCallback(() => {
    if (isSurveyDesignFieldType(question.type)) {
      return <SurveyDesignField question={question} />;
    }

    switch (question.type) {
      case EnumSurveyQuestionType.ShortText:
        return withNameHeader(
          <Input
            value={response[0] ?? ""}
            onChange={e => onResponseChange(question._id, [e.target.value])}
            label={inputLabel}
            hint={question.required ? t("REQUIRED") : undefined}
            _labelProps={{
              visual: "bodyMedium",
              color: "text.dark",
              whiteSpace: "pre-wrap",
            }}
          />,
        );
      case EnumSurveyQuestionType.LongText:
        return withNameHeader(
          <Textarea
            value={response[0] ?? ""}
            onChange={e => onResponseChange(question._id, [e.target.value])}
            label={inputLabel}
            hint={question.required ? t("REQUIRED") : undefined}
            _labelProps={{
              visual: "bodyMedium",
              color: "text.dark",
              whiteSpace: "pre-wrap",
            }}
          />,
        );
      case EnumSurveyQuestionType.RichText:
        return (
          <Flex flexDir="column" gap="50">
            {nameHeader}
            <Suspense fallback={null}>
              <TextEditor
                value={response[0] ?? ""}
                onChange={val => onResponseChange(question._id, [val])}
              />
            </Suspense>
            {question.required && (
              <Text visual="smallRegular" color="text.mid">
                {t("REQUIRED")}
              </Text>
            )}
          </Flex>
        );
      case EnumSurveyQuestionType.Select: {
        const options = _.compact(question.options).map(op => ({
          label: getMultilingualString(op.label),
          value: getMultilingualString(op.label),
        }));
        const collection = Select.createCollection({ items: options });
        return withNameHeader(
          <Select.Root
            collection={collection}
            value={_.compact(response) ?? []}
            onValueChange={({ value }: { value: string[] }) =>
              onResponseChange(question._id, value)
            }
            label={inputLabel}
            hint={question.required ? t("REQUIRED") : undefined}
            _labelProps={{
              className: css({
                fontSize: "md!",
                lineHeight: "md!",
                fontWeight: "medium!",
                color: "text.dark!",
                whiteSpace: "pre-wrap",
              }),
            }}
          >
            {collection.items.map(op => (
              <Select.Item key={op.value} item={op} />
            ))}
          </Select.Root>,
        );
      }
      case EnumSurveyQuestionType.Boolean: {
        if (isMultiOptionBooleanQuestion(question)) {
          const options = getActiveSurveyQuestionOptions(question);
          const selectedValues = _.compact(response) as string[];
          const atMax = !!maxChoices && selectedValues.length >= maxChoices;

          return (
            <Flex flexDir="column" gap="150">
              {nameHeader}
              <Flex flexDir="column" gap="150">
                {options.map((option, i) => (
                  <Checkbox
                    key={`${option}-${i}`}
                    onCheckedChange={(d) => {
                      const next = d.checked
                        ? [...selectedValues, option]
                        : selectedValues.filter(value => value !== option);
                      onResponseChange(question._id, next);
                    }}
                    id={`${inputId}-${i}`}
                    checked={selectedValues.includes(option)}
                    disabled={atMax && !selectedValues.includes(option)}
                    checkboxFill={"white"}
                    label={option}
                    _labelProps={{
                      visual: "bodyMedium",
                      color: "text.dark",
                      whiteSpace: "pre-wrap",
                    }}
                  />
                ))}
              </Flex>
              {(question.required || !!maxChoices) && (
                <Text visual="smallRegular" color="text.mid">
                  {[
                    question.required ? t("REQUIRED") : undefined,
                    maxChoices ? t("MAX_CHOICES", { count: maxChoices }) : undefined,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </Text>
              )}
            </Flex>
          );
        }

        return (
          <Checkbox
            onCheckedChange={(d) =>
              onResponseChange(question._id, [d.checked.toString()])
            }
            id={inputId}
            checked={response[0] === "true"}
            checkboxFill={"white"}
            label={questionName}
            hint={
              [description, question.required ? t("REQUIRED") : undefined]
                .filter(Boolean)
                .join(" · ") || undefined
            }
            _labelProps={{
              visual: "bodyMedium",
              color: "text.dark",
              whiteSpace: "pre-wrap",
            }}
          />
        );
      }
      case EnumSurveyQuestionType.Toggle:
        return withNameHeader(
          <Switch
            checked={response[0] === "true"}
            onCheckedChange={d =>
              onResponseChange(question._id, [d.checked.toString()])
            }
            label={inputLabel}
          />,
        );
      case EnumSurveyQuestionType.Date:
        return (
          <Flex flexDir="column" gap="50">
            {nameHeader}
            <DateInput
              value={response[0] ?? undefined}
              onValueChange={val => onResponseChange(question._id, [val])}
              canSelectPastDates
              placeholder={t("DATE_PLACEHOLDER")}
            />
            {question.required && (
              <Text visual="smallRegular" color="text.mid">
                {t("REQUIRED")}
              </Text>
            )}
          </Flex>
        );
      case EnumSurveyQuestionType.WhereaboutsSearch:
        return (
          <WhereaboutsSearchInput
            question={question}
            questionName={questionName}
            response={response}
            onResponseChange={onResponseChange}
            organizationId={organizationId}
          />
        );
      case EnumSurveyQuestionType.AddressSearch:
        return (
          <AddressSearchInput
            question={question}
            questionName={questionName}
            response={response}
            onResponseChange={onResponseChange}
          />
        );
    }
  }, [
    description,
    inputId,
    maxChoices,
    onResponseChange,
    organizationId,
    question._id,
    question.options,
    question.required,
    question.type,
    questionName,
    response,
    t,
  ]);

  return (
    <Box width={"full"}>
      {getContent()}
      {error ? (
        <Text visual="smallMedium" color="text.error.mid" mt="100">
          {error}
        </Text>
      ) : null}
    </Box>
  );
});
